import React, { Component } from 'react';
import { useNavigate } from 'react-router-dom';
import { Newspaper, FileText, Cable, Loader2, Database, Mail, Upload as UploadIcon, AlertCircle, ArrowRight, TrendingUp } from 'lucide-react';
import { rassegne, connectors } from '../lib/data';
const providerMeta: Record<string, {
  label: string;
  icon: ComponentType<any>;
}> = {
  mimesi: {
    label: 'Mimesi',
    icon: Database
  },
  eco_stampa: {
    label: "L'Eco della Stampa",
    icon: Database
  },
  data_stampa: {
    label: 'Data Stampa',
    icon: Database
  },
  email: {
    label: 'Email',
    icon: Mail
  },
  upload: {
    label: 'Upload manuale',
    icon: UploadIcon
  }
};
function KPI({
  label,
  value,
  icon: Icon,
  accent = false
}: {
  label: string;
  value: string | number;
  icon: React.ElementType;
  accent?: boolean;
}) {
  return <div className="bg-card border border-border rounded-lg p-6 flex flex-col gap-3" data-id="element-803">
      <div className="flex items-center gap-2 text-sm text-muted-foreground font-normal" data-id="element-804">
        <Icon className={`h-4 w-4 ${accent ? 'text-accent animate-spin-slow' : ''}`} data-id="element-805" />
        {label}
      </div>
      <div className={`${`text-3xl md:text-4xl font-light leading-none ${accent ? 'text-accent' : 'text-foreground'}`}`} style={{
      fontVariantNumeric: 'tabular-nums'
    }} data-id="element-806">
        {value}
      </div>
    </div>;
}
function getStatusColor(status: string) {
  if (status === 'connected') return '#4A7C59';
  if (status === 'error') return '#B85450';
  return '#8B8680';
}
function getStatusLabel(status: string) {
  if (status === 'connected') return 'Connesso';
  if (status === 'error') return 'Errore';
  return 'Disconnesso';
}
export function Homepage() {
  const navigate = useNavigate();
  // Calculate aggregates
  const totalRassegne = rassegne.length;
  const totalArticles = rassegne.reduce((acc, r) => acc + r.articleCount, 0);
  const activeConnectors = connectors.filter(c => c.status === 'connected').length;
  const processingRassegne = rassegne.filter(r => r.status === 'processing').length;
  // Campaign summary aggregates
  const weightedScoreSum = rassegne.reduce((acc, r) => acc + r.avgScore * r.articleCount, 0);
  const avgScore = totalArticles > 0 ? weightedScoreSum / totalArticles : 0;
  const totalAVE = rassegne.reduce((acc, r) => acc + r.totalAVE, 0);
  const sentiment = rassegne.reduce((acc, r) => ({
    positive: acc.positive + r.sentimentMix.positive,
    neutral: acc.neutral + r.sentimentMix.neutral,
    negative: acc.negative + r.sentimentMix.negative
  }), {
    positive: 0,
    neutral: 0,
    negative: 0
  });
  return <div className="px-6 py-6 max-w-[1400px] mx-auto w-full space-y-8" data-id="element-807">
      {/* Header */}
      <header data-id="element-808">
        <h1 className="text-2xl md:text-3xl font-normal tracking-tight" data-id="element-809">
          Panoramica
        </h1>
      </header>

      {/* KPI Strip */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-810">
        <KPI label="Rassegne caricate" value={totalRassegne} icon={Newspaper} data-id="element-811" />
        <KPI label="Articoli estratti" value={totalArticles} icon={FileText} data-id="element-812" />
        <KPI label="Connettori attivi" value={activeConnectors} icon={Cable} data-id="element-813" />
        <KPI label="In elaborazione" value={processingRassegne} icon={Loader2} accent data-id="element-814" />
      </div>

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-815">
        {/* Left Column: Connectors */}
        <div className="space-y-6" data-id="element-816">
          <div className="bg-card border border-border rounded-lg flex flex-col h-full" data-id="element-817">
            <div className="px-6 pt-6 pb-3 border-b border-border" data-id="element-818">
              <h3 className="text-lg font-normal" data-id="element-819">Stato connettori</h3>
            </div>
            <div className="flex-1" data-id="element-820">
              {/* Table header */}
              <div className="grid grid-cols-[1fr_100px_100px] gap-2 px-6 py-3 text-[11px] uppercase tracking-wider text-muted-foreground border-b border-border" data-id="element-821">
                <span data-id="element-822">Connettore</span>
                <span className="text-center" data-id="element-823">Stato</span>
                <span className="text-right" data-id="element-824">Rassegne</span>
              </div>
              {/* Table rows */}
              <div className="divide-y divide-border" data-id="element-825">
                {connectors.map(connector => {
                const meta = providerMeta[connector.id];
                const Icon = meta?.icon || Cable;
                return <div key={connector.id} className="grid grid-cols-[1fr_100px_100px] gap-2 items-center px-6 py-4" data-id="element-826">
                      <div className="flex items-center gap-3 min-w-0" data-id="element-827">
                        <div className="w-8 h-8 rounded-md bg-muted flex items-center justify-center flex-shrink-0" data-id="element-828">
                          <Icon className="h-3.5 w-3.5 text-muted-foreground" data-id="element-829" />
                        </div>
                        <div className="min-w-0" data-id="element-830">
                          <div className="text-sm font-normal truncate" data-id="element-831">
                            {connector.name}
                          </div>
                          <div className="text-xs text-muted-foreground mt-0.5" data-id="element-832">
                            {connector.lastSync ? new Date(connector.lastSync).toLocaleDateString('it-IT', {
                          day: 'numeric',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit'
                        }) : 'Mai sincronizzato'}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-center gap-1.5" data-id="element-833">
                        <div className="w-1.5 h-1.5 rounded-full" style={{
                      backgroundColor: getStatusColor(connector.status)
                    }} data-id="element-834" />
                        <span className="text-xs text-muted-foreground" data-id="element-835">
                          {getStatusLabel(connector.status)}
                        </span>
                      </div>
                      <div className="text-sm text-right text-muted-foreground" style={{
                    fontVariantNumeric: 'tabular-nums'
                  }} data-id="element-836">
                        {connector.rassegneIngested}
                      </div>
                    </div>;
              })}
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: Recent Activity */}
        <div className="space-y-6" data-id="element-837">
          <div className="bg-card border border-border rounded-lg flex flex-col h-full" data-id="element-838">
            <div className="flex items-center justify-between px-6 pt-6 pb-3 border-b border-border" data-id="element-839">
              <h3 className="text-lg font-normal" data-id="element-840">Attività recenti</h3>
              <button onClick={() => navigate('/rassegne')} className="text-xs text-accent hover:underline flex items-center gap-1" data-id="element-841">
                Vedi tutte
                <ArrowRight className="h-3 w-3" data-id="element-842" />
              </button>
            </div>

            {/* Table header */}
            <div className="grid grid-cols-[1fr_80px_60px] gap-2 px-6 py-3 text-[11px] uppercase tracking-wider text-muted-foreground border-b border-border" data-id="element-843">
              <span data-id="element-844">Rassegna</span>
              <span className="text-center" data-id="element-845">Periodo</span>
              <span className="text-right" data-id="element-846">Art.</span>
            </div>

            <div className="divide-y divide-border flex-1" data-id="element-847">
              {rassegne.slice(0, 6).map(r => {
              const meta = providerMeta[r.provider];
              const Icon = meta?.icon || Database;
              return <button key={r.id} onClick={() => navigate(`/rassegne/${r.id}`)} className="w-full text-left grid grid-cols-[1fr_80px_60px] gap-2 items-center px-6 py-3.5 hover:bg-muted/30 transition-colors" data-id="element-848">
                    <div className="flex items-center gap-3 min-w-0" data-id="element-849">
                      <div className="w-8 h-8 rounded-md bg-muted flex items-center justify-center flex-shrink-0" data-id="element-850">
                        <Icon className="h-3.5 w-3.5 text-muted-foreground" data-id="element-851" />
                      </div>
                      <div className="min-w-0" data-id="element-852">
                        <div className="text-sm font-normal leading-tight truncate" data-id="element-853">
                          {r.name}
                        </div>
                        <div className="flex items-center gap-1.5 mt-0.5" data-id="element-854">
                          <span className="text-xs text-muted-foreground" data-id="element-855">
                            {meta?.label}
                          </span>
                          {r.status === 'processing' && <div className="flex items-center gap-1 text-accent" data-id="element-856">
                              <Loader2 className="h-3 w-3 animate-spin" data-id="element-857" />
                              <span className="text-[11px]" data-id="element-858">Elab.</span>
                            </div>}
                          {r.status === 'error' && <div className="flex items-center gap-1 text-destructive" data-id="element-859">
                              <AlertCircle className="h-3 w-3" data-id="element-860" />
                              <span className="text-[11px]" data-id="element-861">Errore</span>
                            </div>}
                        </div>
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground text-center" data-id="element-862">
                      {r.dateRange}
                    </div>
                    <div className="text-sm text-right" style={{
                  fontVariantNumeric: 'tabular-nums'
                }} data-id="element-863">
                      {r.articleCount}
                    </div>
                  </button>;
            })}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Full-width: Campaign Summary */}
      <div className="bg-card border border-border rounded-lg p-6" data-id="element-864">
        <h3 className="text-lg font-normal mb-6" data-id="element-865">Andamento campagna</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 divide-y md:divide-y-0 md:divide-x divide-border" data-id="element-866">
          <div className="flex flex-col gap-2 md:px-6 first:px-0 pt-4 md:pt-0 first:pt-0" data-id="element-867">
            <div className="text-sm text-muted-foreground font-normal" data-id="element-868">
              AVE totale
            </div>
            <div className="text-3xl font-light" style={{
            fontVariantNumeric: 'tabular-nums'
          }} data-id="element-869">
              €{(totalAVE / 1000).toFixed(0)}k
            </div>
          </div>

          <div className="flex flex-col gap-2 md:px-6 pt-4 md:pt-0" data-id="element-870">
            <div className="text-sm text-muted-foreground font-normal" data-id="element-871">
              Score medio
            </div>
            <div className="flex items-center gap-3" data-id="element-872">
              <div className="text-3xl font-light text-accent" style={{
              fontVariantNumeric: 'tabular-nums'
            }} data-id="element-873">
                {avgScore.toFixed(1)}
              </div>
              <TrendingUp className="h-5 w-5 text-accent" data-id="element-874" />
            </div>
          </div>

          <div className="flex flex-col gap-2 md:px-6 pt-4 md:pt-0" data-id="element-875">
            <div className="text-sm text-muted-foreground font-normal" data-id="element-876">
              Sentiment (Pos / Neu / Neg)
            </div>
            <div className="flex items-baseline gap-2 leading-none mt-1" data-id="element-877">
              <span className="text-3xl font-light text-[#4A7C59]" style={{
              fontVariantNumeric: 'tabular-nums'
            }} data-id="element-878">
                {sentiment.positive}
              </span>
              <span className="text-xl text-muted-foreground font-light" data-id="element-879">
                /
              </span>
              <span className="text-2xl text-muted-foreground font-light" style={{
              fontVariantNumeric: 'tabular-nums'
            }} data-id="element-880">
                {sentiment.neutral}
              </span>
              <span className="text-xl text-muted-foreground font-light" data-id="element-881">
                /
              </span>
              <span className="text-2xl text-[#B85450] font-light" style={{
              fontVariantNumeric: 'tabular-nums'
            }} data-id="element-882">
                {sentiment.negative}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>;
}